"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get MeetingRecurringSeriesSchema () {
        return MeetingRecurringSeriesSchema;
    },
    get MeetingRecurringSeriesStatusSchema () {
        return MeetingRecurringSeriesStatusSchema;
    },
    get PendingRecurringSeriesCalendarOperationSchema () {
        return PendingRecurringSeriesCalendarOperationSchema;
    },
    get RecurrenceDayOfWeekSchema () {
        return RecurrenceDayOfWeekSchema;
    },
    get RecurrenceFrequencySchema () {
        return RecurrenceFrequencySchema;
    },
    get RecurrenceSchema () {
        return RecurrenceSchema;
    },
    get RecurrenceWeekOfMonthSchema () {
        return RecurrenceWeekOfMonthSchema;
    }
});
const _bson = require("bson");
const _zod = require("zod");
const _base = require("../base");
const _useractionrecord = require("../shared/user-action-record");
const _videoconference = require("../video-conference");
const MeetingRecurringSeriesStatusSchema = _zod.z.enum([
    "active",
    "finished",
    "deleted"
]);
const RecurrenceFrequencySchema = _zod.z.enum([
    "weekday",
    "weekly",
    "monthly",
    "yearly"
]);
const RecurrenceDayOfWeekSchema = _zod.z.enum([
    "MO",
    "TU",
    "WE",
    "TH",
    "FR",
    "SA",
    "SU"
]);
const RecurrenceWeekOfMonthSchema = _zod.z.enum([
    "first",
    "second",
    "third",
    "fourth",
    "last"
]);
const RecurrenceSchema = _zod.z.object({
    frequency: RecurrenceFrequencySchema,
    /** 1 = every period; weekly + 2 = biweekly. */ interval: _zod.z.number().int().min(1).max(4),
    day_of_week: RecurrenceDayOfWeekSchema.optional(),
    /** Monthly only — e.g. "second" + TU = the second Tuesday of each month. */ week_of_month: RecurrenceWeekOfMonthSchema.optional(),
    /** Yearly only — 1-12. */ month: _zod.z.number().int().min(1).max(12).optional(),
    /** Yearly only — 1-31; a Feb 29 anchor only occurs in leap years (RFC 5545 skip). */ day_of_month: _zod.z.number().int().min(1).max(31).optional()
});
/**
 * Provider work owed by a series, retried by the recurring-meeting sweep until it lands
 * or ages out. `claimed_at` leases an operation to one executor: the request that queued
 * it and the sweep both reconcile pending work, and the operations that CREATE a native
 * recurring event must never run twice.
 */ const PendingCalendarOperationBaseSchema = _zod.z.object({
    operation_id: _zod.z.instanceof(_bson.ObjectId),
    queued_at: _zod.z.instanceof(Date),
    claimed_at: _zod.z.instanceof(Date).optional()
});
/** Build the native master for an enabled series that has none yet. */ const PendingCalendarAttachOperationSchema = PendingCalendarOperationBaseSchema.extend({
    type: _zod.z.literal("attach_master"),
    integration_owner_id: _zod.z.instanceof(_bson.ObjectId)
});
/** Trim a retired master at `removal_instant`, or delete it when nothing has elapsed. */ const PendingCalendarCleanupOperationSchema = PendingCalendarOperationBaseSchema.extend({
    type: _zod.z.literal("cleanup_master"),
    integration_owner_id: _zod.z.instanceof(_bson.ObjectId),
    provider: _zod.z.enum([
        "google",
        "outlook"
    ]),
    calendar_id: _zod.z.string(),
    master_event_id: _zod.z.string(),
    master_start_date: _zod.z.instanceof(Date),
    recurrence: RecurrenceSchema,
    timezone: _zod.z.string(),
    start_time_of_day: _zod.z.string(),
    duration_minutes: _zod.z.number().int().positive(),
    series_end_date: _zod.z.instanceof(Date).optional(),
    removal_instant: _zod.z.instanceof(Date)
});
/** Point a materialized meeting at its occurrence on the master. */ const PendingCalendarOccurrenceLinkOperationSchema = PendingCalendarOperationBaseSchema.extend({
    type: _zod.z.literal("link_occurrence"),
    meeting_id: _zod.z.instanceof(_bson.ObjectId),
    integration_owner_id: _zod.z.instanceof(_bson.ObjectId),
    provider: _zod.z.enum([
        "google",
        "outlook"
    ]),
    calendar_id: _zod.z.string(),
    master_event_id: _zod.z.string(),
    occurrence_start: _zod.z.instanceof(Date),
    timezone: _zod.z.string(),
    /** Exact meeting pointer the retry may replace; absent means the meeting must stay unlinked. */ expected_calendar_event_id: _zod.z.string().optional(),
    /** Verified standalone event to remove only after the recurring occurrence is linked. */ standalone_event_id_to_remove: _zod.z.string().optional()
});
/** Re-express a schedule edit on the calendar; a different linked master means newer work won. */ const PendingCalendarMasterReplacementOperationSchema = PendingCalendarOperationBaseSchema.extend({
    type: _zod.z.literal("replace_master"),
    expected_master_event_id: _zod.z.string()
});
const PendingRecurringSeriesCalendarOperationSchema = _zod.z.discriminatedUnion("type", [
    PendingCalendarAttachOperationSchema,
    PendingCalendarCleanupOperationSchema,
    PendingCalendarOccurrenceLinkOperationSchema,
    PendingCalendarMasterReplacementOperationSchema
]);
const MeetingRecurringSeriesSchema = _base.BaseDocument.extend({
    /** Foreign key - meeting_workspaces document */ meeting_workspace_id: _zod.z.instanceof(_bson.ObjectId),
    // Template applied to every materialized meeting
    name: _zod.z.string(),
    description: _zod.z.string(),
    status: MeetingRecurringSeriesStatusSchema,
    recurrence: RecurrenceSchema,
    // Series anchor. Meetings store plain UTC instants, so the series carries its own
    // wall-clock time + IANA timezone; occurrence math runs in this zone (DST-safe).
    timezone: _zod.z.string(),
    /** Wall-clock start in the anchor timezone, "HH:mm". */ start_time_of_day: _zod.z.string(),
    duration_minutes: _zod.z.number().int().positive(),
    series_start_date: _zod.z.instanceof(Date),
    /** Absent = the series never ends. */ series_end_date: _zod.z.instanceof(Date).optional(),
    /** Series-level always/never carryover of unchecked items into each new meeting. */ carry_over: _zod.z.boolean(),
    /**
	 * The single identity whose calendar/video-conferencing tokens the series uses
	 * (the series creator in v1). Materialization runs as this user.
	 */ integration_owner_id: _zod.z.instanceof(_bson.ObjectId),
    add_to_calendar: _zod.z.boolean(),
    /**
	 * The native recurring event on the owner's calendar. Retroactive attach and series
	 * edits anchor the master mid-series, so it carries its own first-occurrence start and
	 * a snapshot of the rule it was created with: trims must speak the master's cadence,
	 * not whatever the series was edited to since.
	 */ calendar_series: _zod.z.object({
        provider: _zod.z.enum([
            "google",
            "outlook"
        ]),
        calendar_id: _zod.z.string(),
        master_event_id: _zod.z.string(),
        master_start_date: _zod.z.instanceof(Date),
        recurrence: RecurrenceSchema,
        timezone: _zod.z.string(),
        start_time_of_day: _zod.z.string(),
        duration_minutes: _zod.z.number().int().positive(),
        /** The end bound of this specific native master, not the series' later edited value. */ series_end_date: _zod.z.instanceof(Date).optional()
    }).optional(),
    /** Internal write fence for occurrence-link commits; not a content revision. */ calendar_operation_commit_version: _zod.z.number().int().nonnegative().optional(),
    /** Durable provider work retried by the recurring-meeting sweep. */ pending_calendar_operations: _zod.z.array(PendingRecurringSeriesCalendarOperationSchema).optional(),
    video_conferencing_provider: _zod.z.enum(_videoconference.VIDEO_CONFERENCING_PROVIDERS).optional(),
    // Materialization pointers
    current_meeting_id: _zod.z.instanceof(_bson.ObjectId).optional(),
    /** Start of the NEXT (unmaterialized) occurrence. */ next_occurrence_at: _zod.z.instanceof(Date),
    /** Current meeting's end + 2h; re-stamped when the current meeting is rescheduled. */ next_materialization_at: _zod.z.instanceof(Date),
    created: _useractionrecord.UserActionRecord,
    updated: _useractionrecord.UserActionRecord,
    /** Soft delete record. */ deleted: _useractionrecord.UserActionRecord.optional()
});
